import { useMemo, useCallback } from 'react'
import { Table, Thead, Tbody, Tr, Td, Th, TableScrollHost } from '@/common/ui/Table'
import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import type { GenerationCriteriaAvgScoreSchema, ProductCardLlmBlockTypes } from '@/common/api/client/types.gen'
import {
    exportDetailedComparisonToExcel,
    getBlockDisplayValue,
    getCriteriaDisplayValue,
    type DetailedBrandComparisonRow,
    type DetailedComparisonFormat,
    type ExcelColumn,
} from '../../lib/detailedBrandComparison'
import {
    BLOCK_TITLE_TEXT_CLASS,
    BLOCK_TOP_BORDER,
    DASHBOARD_BLOCK_TYPES,
    formatScoreValue,
} from '../../lib/scoreColors'
import { getBlockTitle, getCriteriaLabel } from '../../lib/labels'
import { CompanyStatsExcelExportButton } from '../CompanyStatsExcelExportButton'
import {
    DetailedBrandComparisonBlockProgressCell,
    DetailedBrandComparisonPresenceCell,
} from './DetailedBrandComparisonCells'
import type { CompanyStatsComparisonMode } from '../../model/useCompanyStatsBrandComparison'

type CompanyStatsDetailedBrandComparisonCriteriaTablesProps = {
    compareBy: CompanyStatsComparisonMode
    rows: DetailedBrandComparisonRow[]
    format: DetailedComparisonFormat
    isLoading?: boolean
}

type CriteriaColumn = {
    criteriaName: string
    label: string
    sample: GenerationCriteriaAvgScoreSchema
}

const collectCriteriaColumns = (
    rows: DetailedBrandComparisonRow[],
    blockType: ProductCardLlmBlockTypes
): CriteriaColumn[] => {
    const map = new Map<string, CriteriaColumn>()
    for (const row of rows) {
        const block = row.blocks.find(item => item.blockType === blockType)
        for (const criteria of block?.criterias ?? []) {
            if (!map.has(criteria.criteria_name)) {
                map.set(criteria.criteria_name, {
                    criteriaName: criteria.criteria_name,
                    label: getCriteriaLabel(criteria.title, criteria.title_type, criteria.criteria_name),
                    sample: criteria,
                })
            }
        }
    }
    return Array.from(map.values())
}

const BlockCriteriaTable = ({
    compareBy,
    blockType,
    title,
    rows,
    format,
    isLoading,
}: {
    compareBy: CompanyStatsComparisonMode
    blockType: ProductCardLlmBlockTypes
    title: string
    rows: DetailedBrandComparisonRow[]
    format: DetailedComparisonFormat
    isLoading: boolean
}) => {
    const emptySelectionLabel = compareBy === 'brand' ? 'один бренд' : 'одного продавца'
    const criteriaColumns = useMemo(() => collectCriteriaColumns(rows, blockType), [rows, blockType])
    const topBorder = BLOCK_TOP_BORDER[blockType]
    const sampleBlock = rows[0]?.blocks.find(block => block.blockType === blockType)

    const handleExport = useCallback(() => {
        const columns: ExcelColumn[] = [
            { header: '#', getValue: (_row, index) => index + 1 },
            { header: 'Бренд', getValue: row => row.name },
            { header: 'Товаров', getValue: row => row.productCount },
            {
                header: `Общая оценка блока / ${formatScoreValue(sampleBlock?.maxScore ?? 0)}`,
                getValue: row => {
                    const block = row.blocks.find(item => item.blockType === blockType)
                    if (!block) return '—'
                    return getBlockDisplayValue(block, format).display
                },
            },
            ...criteriaColumns.map(col => ({
                header: `${col.label} % присутствия`,
                getValue: (row: DetailedBrandComparisonRow) => {
                    const block = row.blocks.find(item => item.blockType === blockType)
                    const criteria = block?.criterias.find(item => item.criteria_name === col.criteriaName)
                    if (!criteria) return '—'
                    return getCriteriaDisplayValue(criteria, format).display
                },
            })),
        ]
        exportDetailedComparisonToExcel(rows, columns, `brand_comparison_${blockType}`)
    }, [blockType, criteriaColumns, format, rows, sampleBlock?.maxScore])

    if (criteriaColumns.length === 0 && !isLoading && rows.length > 0) return null

    return (
        <Card color='primary' topBorderColor={topBorder} tableScroll>
            <div className='mb-4 flex items-center justify-between gap-4'>
                <h4
                    className={cn(
                        'text-xs font-bold uppercase',
                        topBorder ? BLOCK_TITLE_TEXT_CLASS[topBorder] : 'text-base-content'
                    )}
                >
                    {title}
                </h4>
                <CompanyStatsExcelExportButton
                    onClick={handleExport}
                    disabled={rows.length === 0}
                    ariaLabel={`Экспорт ${title} в Excel`}
                />
            </div>

            <TableScrollHost minHeight='8rem'>
                <Table pinRows striped thBg rounded={false} className='table-pin-left-2 w-full min-w-[48rem]'>
                    <Thead>
                        <Tr>
                            {[
                                <Th key='index' className='w-10'>
                                    #
                                </Th>,
                                <Th key='brand'>Бренд</Th>,
                                <Th key='products' className='text-center'>
                                    Товаров
                                </Th>,
                                <Th key='blockOverall'>
                                    <span className='flex flex-col gap-0.5'>
                                        <span>Общая оценка блока</span>
                                        <span className='font-normal normal-case text-base-content/50'>
                                            / {formatScoreValue(sampleBlock?.maxScore ?? 0)}
                                        </span>
                                    </span>
                                </Th>,
                                ...criteriaColumns.map(col => (
                                    <Th key={col.criteriaName} className='text-center'>
                                        <span className='flex flex-col items-center gap-0.5'>
                                            <span>{col.label}</span>
                                            <span className='font-normal normal-case text-base-content/50'>
                                                % присутствия
                                            </span>
                                        </span>
                                    </Th>
                                )),
                            ]}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading && rows.length === 0 ? (
                            <Tr>
                                <Td
                                    colSpan={4 + criteriaColumns.length}
                                    className='py-8 text-center text-base-content/70'
                                >
                                    <span className='loading loading-spinner loading-md' aria-hidden />
                                </Td>
                            </Tr>
                        ) : rows.length === 0 ? (
                            <Tr>
                                <Td
                                    colSpan={4 + criteriaColumns.length}
                                    className='py-8 text-center text-base-content/60'
                                >
                                    {`Выберите хотя бы ${emptySelectionLabel}`}
                                </Td>
                            </Tr>
                        ) : (
                            rows.map((row, index) => {
                                const block = row.blocks.find(item => item.blockType === blockType)
                                const blockValue = block ? getBlockDisplayValue(block, format) : null

                                return (
                                    <Tr key={row.id}>
                                        {[
                                            <Td key='index' className='tabular-nums text-base-content/60'>
                                                {index + 1}
                                            </Td>,
                                            <Td key='brand'>
                                                <span className='inline-flex min-w-0 items-center gap-2'>
                                                    <span
                                                        className='inline-block h-2 w-2 shrink-0 rounded-full'
                                                        style={{ backgroundColor: row.color }}
                                                        aria-hidden
                                                    />
                                                    <span className='truncate text-sm'>{row.name}</span>
                                                </span>
                                            </Td>,
                                            <Td key='products' className='text-center tabular-nums'>
                                                {row.productCount}
                                            </Td>,
                                            <Td key='blockOverall'>
                                                {blockValue ? (
                                                    <DetailedBrandComparisonBlockProgressCell
                                                        percent={blockValue.progressPercent}
                                                        display={blockValue.display}
                                                        color={
                                                            topBorder === 'purple'
                                                                ? '#8b5cf6'
                                                                : topBorder === 'blue'
                                                                  ? '#3b82f6'
                                                                  : topBorder === 'green'
                                                                    ? '#08ac81'
                                                                    : '#f59e0b'
                                                        }
                                                        label={`${row.name}, ${title}`}
                                                    />
                                                ) : (
                                                    <span className='text-base-content/40'>—</span>
                                                )}
                                            </Td>,
                                            ...criteriaColumns.map(col => {
                                                const criteria = block?.criterias.find(
                                                    item => item.criteria_name === col.criteriaName
                                                )
                                                if (!criteria) {
                                                    return (
                                                        <Td
                                                            key={col.criteriaName}
                                                            className='text-center text-base-content/40'
                                                        >
                                                            —
                                                        </Td>
                                                    )
                                                }
                                                const value = getCriteriaDisplayValue(criteria, format)
                                                return (
                                                    <Td key={col.criteriaName} className='text-center'>
                                                        <DetailedBrandComparisonPresenceCell
                                                            display={value.display}
                                                            percent={value.percent}
                                                            label={`${row.name}, ${col.label}`}
                                                            format={format}
                                                        />
                                                    </Td>
                                                )
                                            }),
                                        ]}
                                    </Tr>
                                )
                            })
                        )}
                    </Tbody>
                </Table>
            </TableScrollHost>
        </Card>
    )
}

export const CompanyStatsDetailedBrandComparisonCriteriaTables = ({
    compareBy,
    rows,
    format,
    isLoading = false,
}: CompanyStatsDetailedBrandComparisonCriteriaTablesProps) => {
    const emptySelectionLabel = compareBy === 'brand' ? 'один бренд' : 'одного продавца'
    const blocks = useMemo(() => {
        const sampleRow = rows[0]
        return DASHBOARD_BLOCK_TYPES.map(blockType => {
            const block = sampleRow?.blocks.find(item => item.blockType === blockType)
            return {
                blockType,
                title: getBlockTitle(blockType, block?.title),
            }
        })
    }, [rows])

    if (rows.length === 0) {
        return (
            <Card color='primary'>
                {isLoading ? (
                    <div className='flex items-center justify-center py-8'>
                        <span className='loading loading-spinner loading-md' aria-label='Загрузка таблицы' />
                    </div>
                ) : (
                    <p className='py-8 text-center text-base-content/60'>{`Выберите хотя бы ${emptySelectionLabel}`}</p>
                )}
            </Card>
        )
    }

    return (
        <div className='flex flex-col gap-4'>
            {blocks.map(block => (
                <BlockCriteriaTable
                    key={block.blockType}
                    compareBy={compareBy}
                    blockType={block.blockType}
                    title={block.title}
                    rows={rows}
                    format={format}
                    isLoading={isLoading}
                />
            ))}
        </div>
    )
}
